import { createStore, useStore } from 'zustand';
import { GeneralEnterprisePrivileges, GeneralPlatformPrivileges } from './types';

export type Privilege = keyof GeneralEnterprisePrivileges;
export type PlatformPrivilege = keyof GeneralPlatformPrivileges;

export type PrivilegeMap = {
    [privilege in Privilege]: boolean;
};

export type PlatformPrivilegeMap = {
    [privilege in PlatformPrivilege]: boolean;
};

interface PrivilegesStore {
    privileges: PrivilegeMap;
    platformPrivileges: PlatformPrivilegeMap;
    setPrivileges: (privileges: PrivilegeMap) => void;
    setPlatformPrivileges: (platformPrivilege: PlatformPrivilegeMap) => void;
}

/**
 * Enterprise privileges can be "overridden" if Platform profile has `accessAllEnterprises` privilege;
 * we have the `defaultValue` for that.
 */
export const createPrivilegesMap = (
    privileges: Partial<PrivilegeMap>,
    defaultValue = false
): GeneralEnterprisePrivileges => ({
    admin: privileges.admin ?? defaultValue,
    configureEnterprise: privileges.configureEnterprise ?? defaultValue,
    manageServices: privileges.manageServices ?? defaultValue,
    manageAvailability: privileges.manageAvailability ?? defaultValue,
    manageResourceCategories: privileges.manageResourceCategories ?? defaultValue,
    manageProducts: privileges.manageProducts ?? defaultValue,
    manageBusinessSegments: privileges.manageBusinessSegments ?? defaultValue,
    manageRateGroups: privileges.manageRateGroups ?? defaultValue,
    manageProductRules: privileges.manageProductRules ?? defaultValue,
    manageRates: privileges.manageRates ?? defaultValue,
    manageRestrictions: privileges.manageRestrictions ?? defaultValue,
    manageServiceConfigurations: privileges.manageServiceConfigurations ?? defaultValue,
    manageSpaces: privileges.manageSpaces ?? defaultValue,
    overrideRestrictions: privileges.overrideRestrictions ?? defaultValue,
    manageAccounts: privileges.manageAccounts ?? defaultValue,
    manageCompanyContracts: privileges.manageCompanyContracts ?? defaultValue,
    manageReservations: privileges.manageReservations ?? defaultValue,
    performHousekeeping: privileges.performHousekeeping ?? defaultValue,
    issueInvoices: privileges.issueInvoices ?? defaultValue,
    overbookServices: privileges.overbookServices ?? defaultValue,
    manageEmployees: privileges.manageEmployees ?? defaultValue,
    chargeCardsOnline: privileges.chargeCardsOnline ?? defaultValue,
    manageInvoiceability: privileges.manageInvoiceability ?? defaultValue,
    viewSensitiveReports: privileges.viewSensitiveReports ?? defaultValue,
    manageAccountingCategories: privileges.manageAccountingCategories ?? defaultValue,
    manageAccountingConfiguration: privileges.manageAccountingConfiguration ?? defaultValue,
    manageCounters: privileges.manageCounters ?? defaultValue,
    manageDepartments: privileges.manageDepartments ?? defaultValue,
    manageOutlets: privileges.manageOutlets ?? defaultValue,
    rebateItems: privileges.rebateItems ?? defaultValue,
    viewCustomerData: privileges.viewCustomerData ?? defaultValue,
    viewCompanyData: privileges.viewCompanyData ?? defaultValue,
    manageLoyaltyMemberships: privileges.manageLoyaltyMemberships ?? defaultValue,
    manageMailQueue: privileges.manageMailQueue ?? defaultValue,
    overrideRatePrice: privileges.overrideRatePrice ?? defaultValue,
    manageAvailabilityBlockRates: privileges.manageAvailabilityBlockRates ?? defaultValue,
    manageSellLimits: privileges.manageSellLimits ?? defaultValue,
    requestInstantPayouts: privileges.requestInstantPayouts ?? defaultValue,
    clearCustomerData: privileges.clearCustomerData ?? defaultValue,
    managePaymentAndCancellationPolicies: privileges.managePaymentAndCancellationPolicies ?? defaultValue,
    manageMailTemplates: privileges.manageMailTemplates ?? defaultValue,
});

/**
 * Platform privileges needs to be set explicitly, because the current implementation often use a privilege check to actually
 * determine whether you're located or not in the Platform section (e.g. to display links in menu).
 */
export const createPlatformPrivilegeMap = (privileges: Partial<PlatformPrivilegeMap>): GeneralPlatformPrivileges => ({
    admin: privileges.admin ?? false,
    runInstallation: privileges.runInstallation ?? false,
    manageContent: privileges.manageContent ?? false,
    managePlatformProfiles: privileges.managePlatformProfiles ?? false,
    manageEnterprises: privileges.manageEnterprises ?? false,
    manageSubscriptions: privileges.manageSubscriptions ?? false,
    manageCharitySubscriptions: privileges.manageCharitySubscriptions ?? false,
    accessAllEnterprises: privileges.accessAllEnterprises ?? false,
    extendEHW: privileges.extendEHW ?? false,
    manageProfiles: privileges.manageProfiles ?? false,
    manageChannelManagerConfigurations: privileges.manageChannelManagerConfigurations ?? false,
});

const privilegesStore = createStore<PrivilegesStore>((set) => ({
    privileges: createPrivilegesMap({}),
    platformPrivileges: createPlatformPrivilegeMap({}),
    setPrivileges: (privileges) => set({ privileges }),
    setPlatformPrivileges: (platformPrivileges) => set({ platformPrivileges }),
}));

export const { setPrivileges, setPlatformPrivileges } = privilegesStore.getState();

export const usePrivilege = (privilege: Privilege) => useStore(privilegesStore).privileges[privilege];

export const usePlatformPrivilege = (platformPrivilege: PlatformPrivilege) =>
    useStore(privilegesStore).platformPrivileges[platformPrivilege];

/**
 * Used to access Privileges outside React components
 * @deprecated Prefer {@link usePrivilege} instead.
 */
export const hasPrivilege = (privilege: Privilege) => privilegesStore.getState().privileges[privilege];

/**
 * Used to access Platform Privileges outside React components
 * @deprecated Prefer {@link usePlatformPrivilege} instead.
 */
export const hasPlatformPrivilege = (platformPrivilege: PlatformPrivilege) =>
    privilegesStore.getState().platformPrivileges[platformPrivilege];
